import React, { useState } from 'react';
import {
  Lock,
  ShieldCheck,
  LogOut,
  Loader2,
  Check,
  X,
  Instagram,
  Clock,
  MessageSquare,
  AlertTriangle,
  ChevronRight,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { ScheduledPost, approvePost, rejectPost } from '@/lib/brandStudio';
import {
  isApprovalAdmin,
  loginApprovalAdmin,
  logoutApprovalAdmin,
  APPROVAL_ADMIN_USERNAME,
} from '@/lib/approvalAdmin';

type Step = 1 | 2 | 3 | 4 | 5;

interface ApprovalStepProps {
  posts: ScheduledPost[];
  refresh: () => Promise<void>;
  fmtDateTime: (iso: string | null) => string;
  goToStep: (s: Step) => void;
}

const statusBadge: Record<string, string> = {
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  rejected: 'bg-red-50 text-red-700 border-red-300',
  pending: 'bg-amber-50 text-amber-700 border-amber-300',
};

const ApprovalStep: React.FC<ApprovalStepProps> = ({ posts, refresh, fmtDateTime, goToStep }) => {
  const [authed, setAuthed] = useState(isApprovalAdmin());

  if (!authed) {
    return <AdminLoginGate onSuccess={() => setAuthed(true)} />;
  }

  return (
    <ApprovalBoard
      posts={posts}
      refresh={refresh}
      fmtDateTime={fmtDateTime}
      goToStep={goToStep}
      onLogout={() => {
        logoutApprovalAdmin();
        setAuthed(false);
      }}
    />
  );
};

// -------- Login gate --------

const AdminLoginGate: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setTimeout(() => {
      if (loginApprovalAdmin(username.trim(), password)) {
        onSuccess();
      } else {
        setError('Invalid administrator credentials.');
      }
      setBusy(false);
    }, 300);
  };

  return (
    <div className="max-w-md mx-auto py-10">
      <div className="text-center mb-6">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#0D1B2A] flex items-center justify-center mb-3">
          <Lock className="w-6 h-6 text-[#C9A961]" />
        </div>
        <h3 className="font-serif text-2xl text-[#0D1B2A] font-light">Step 4 · Administrator Approval</h3>
        <p className="font-sans text-sm text-slate-600 mt-2">
          This area is restricted. Sign in to review, approve, or send posts back for revision.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="border border-slate-200 bg-white p-6 space-y-4">
        {error && (
          <div className="flex items-center gap-2 border border-red-300 bg-red-50 px-3 py-2">
            <X className="w-4 h-4 text-red-500" />
            <span className="font-sans text-xs text-red-600">{error}</span>
          </div>
        )}
        <div>
          <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-slate-400">Username</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            className="mt-1 w-full border border-slate-200 px-3 py-2 font-sans text-sm focus:border-[#C9A961] outline-none"
            placeholder="Administrator username"
          />
        </div>
        <div>
          <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-slate-400">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="mt-1 w-full border border-slate-200 px-3 py-2 font-sans text-sm focus:border-[#C9A961] outline-none"
            placeholder="••••••••"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="w-full font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-3 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          Sign In to Review
        </button>
      </form>
    </div>
  );
};

// -------- Approval board --------

interface BoardProps extends ApprovalStepProps {
  onLogout: () => void;
}

const ApprovalBoard: React.FC<BoardProps> = ({ posts, refresh, fmtDateTime, goToStep, onLogout }) => {
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [feedbackDraft, setFeedbackDraft] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const counts = {
    pending: posts.filter((p) => (p.approval_status || 'pending') === 'pending').length,
    approved: posts.filter((p) => p.approval_status === 'approved').length,
    rejected: posts.filter((p) => p.approval_status === 'rejected').length,
  };

  const filtered = posts.filter((p) => {
    const st = p.approval_status || 'pending';
    if (filter === 'all') return true;
    return st === filter;
  });

  const handleApprove = async (p: ScheduledPost) => {
    setBusyId(p.id);
    await approvePost(p.id, APPROVAL_ADMIN_USERNAME, feedbackDraft[p.id]);
    await refresh();
    setBusyId(null);
  };

  const handleReject = async (p: ScheduledPost) => {
    const fb = (feedbackDraft[p.id] || '').trim();
    if (!fb) {
      alert('Please add feedback explaining why this post is being sent back.');
      return;
    }
    setBusyId(p.id);
    await rejectPost(p.id, APPROVAL_ADMIN_USERNAME, fb);
    await refresh();
    setBusyId(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-emerald-700">
              Signed in as {APPROVAL_ADMIN_USERNAME}
            </span>
          </div>
          <h3 className="font-serif text-2xl text-[#0D1B2A] font-light mt-1">Step 4 · Approve Generated Posts</h3>
          <p className="font-sans text-sm text-slate-600 mt-2 max-w-2xl">
            Approve posts to advance them, or disapprove with feedback to send them back to Step 3 for revision.
          </p>
        </div>
        <button
          onClick={onLogout}
          className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-500 transition-colors flex items-center gap-2"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign Out
        </button>
      </div>

      {/* Stat tiles / filters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {([
          { key: 'pending', label: 'Awaiting Review', value: counts.pending, color: 'text-amber-700' },
          { key: 'approved', label: 'Approved', value: counts.approved, color: 'text-emerald-700' },
          { key: 'rejected', label: 'Sent Back', value: counts.rejected, color: 'text-red-600' },
          { key: 'all', label: 'All Posts', value: posts.length, color: 'text-[#0D1B2A]' },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            className={`text-left border p-4 transition-all ${
              filter === t.key ? 'border-[#C9A961] bg-[#C9A961]/5' : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <div className={`font-serif text-2xl ${t.color}`}>{t.value}</div>
            <div className="font-sans text-[10px] tracking-[0.15em] uppercase text-slate-500 mt-1">{t.label}</div>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-200 py-16 text-center bg-slate-50">
          <ThumbsUp className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          <p className="font-sans text-sm text-slate-400">
            {filter === 'pending' ? 'Nothing awaiting review. All caught up.' : 'No posts in this view.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((p) => {
            const st = p.approval_status || 'pending';
            const busy = busyId === p.id;
            return (
              <div key={p.id} className="border border-slate-200 bg-white overflow-hidden">
                <div className="grid md:grid-cols-[180px_1fr]">
                  {/* Image */}
                  <div className="aspect-square md:aspect-auto bg-slate-100 relative">
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-3 text-center">
                        <Instagram className="w-6 h-6 text-slate-300 mb-2" />
                        <p className="font-sans text-[10px] text-slate-400">No image</p>
                      </div>
                    )}
                    <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/60 text-white font-sans text-[9px] tracking-wider uppercase rounded">
                      {p.post_type}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <Instagram className="w-3.5 h-3.5 text-[#C9A961]" />
                          <span className="font-serif text-base text-[#0D1B2A]">{p.day_label || 'Post'}</span>
                          {p.theme && (
                            <span className="font-sans text-[10px] tracking-wider uppercase text-slate-400">· {p.theme}</span>
                          )}
                        </div>
                        <span className="flex items-center gap-1 text-[11px] text-slate-500 mt-1">
                          <Clock className="w-3 h-3" /> {fmtDateTime(p.scheduled_for)}
                        </span>
                      </div>
                      <span
                        className={`font-sans text-[10px] tracking-wider uppercase px-2 py-0.5 border rounded-full ${
                          statusBadge[st]
                        }`}
                      >
                        {st === 'pending' ? 'Awaiting Review' : st === 'rejected' ? 'Sent Back' : 'Approved'}
                      </span>
                    </div>

                    <p className="font-sans text-sm text-slate-700 leading-relaxed whitespace-pre-line line-clamp-4">
                      {p.caption || <span className="italic text-slate-400">No caption</span>}
                    </p>

                    {p.hashtags && p.hashtags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {p.hashtags.slice(0, 6).map((h) => (
                          <span
                            key={h}
                            className="font-sans text-[10px] text-[#1A3B5C] bg-[#F3EFE6] border border-[#C9A961]/30 px-2 py-0.5 rounded-full"
                          >
                            {h}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Existing feedback (for already-reviewed posts) */}
                    {p.admin_feedback && st !== 'pending' && (
                      <div
                        className={`mt-3 flex items-start gap-2 border-l-2 pl-3 py-1 ${
                          st === 'rejected' ? 'border-red-400' : 'border-emerald-400'
                        }`}
                      >
                        <MessageSquare className="w-3.5 h-3.5 mt-0.5 text-slate-400 flex-shrink-0" />
                        <div>
                          <div className="font-sans text-[10px] tracking-wider uppercase text-slate-400">
                            Admin feedback
                          </div>
                          <p className="font-sans text-xs text-slate-700 mt-0.5">{p.admin_feedback}</p>
                        </div>
                      </div>
                    )}

                    {st === 'rejected' && (
                      <div className="mt-2 flex items-center gap-2 text-amber-700">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        <span className="font-sans text-[11px]">
                          Sent back to Step 3 (Draft) for revision.
                        </span>
                        <button
                          onClick={() => goToStep(3)}
                          className="font-sans text-[10px] tracking-wider uppercase text-[#C9A961] hover:underline flex items-center gap-0.5"
                        >
                          Open Step 3 <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    )}

                    {/* Review controls */}
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <textarea
                        value={feedbackDraft[p.id] ?? (st !== 'pending' ? p.admin_feedback || '' : '')}
                        onChange={(e) => setFeedbackDraft({ ...feedbackDraft, [p.id]: e.target.value })}
                        placeholder="Feedback for the team (required to disapprove, optional to approve)…"
                        className="w-full border border-slate-200 px-3 py-2 font-sans text-sm h-16 resize-none focus:border-[#C9A961] outline-none"
                      />
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => handleApprove(p)}
                          disabled={busy}
                          className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center gap-1.5 disabled:opacity-60"
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />}
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(p)}
                          disabled={busy}
                          className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-red-300 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-1.5 disabled:opacity-60"
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsDown className="w-3.5 h-3.5" />}
                          Disapprove & Send Back
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApprovalStep;
